import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    TextField,
    IconButton,
    Button,
    InputAdornment,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import clsx from 'clsx';
import './CustomMobileDateTimePicker.scss';
import { LanguageContext } from 'context/LanguageContext';
import { useDispatch, useSelector } from 'react-redux';
import { reorder } from 'modules/basket/actions';
import { useLocation } from 'react-router-dom';
import { isDeliveryMode } from 'utils/appUtils';
import Util from 'utils/Util';
import BasketUpdateDialog from '../basketUpdateWarningAlert';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const TIME_SLOT_INTERVAL_MINUTES = 30;

const getSafeLeadValue = (val, fallback) => {
    const parsed = Number(val);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const generateAllDatesInRange = (minLeadDays, maxLeadDays, storeTimezone) => {
    const dates = [];
    const safeMax = Math.max(maxLeadDays, 1);
    // Get today's date string in store timezone, then create naive dayjs object
    // This prevents timezone conversion issues when .toDate() is called later
    const todayInStoreTimezone = storeTimezone 
        ? dayjs().tz(storeTimezone).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
    // minLeadDays represents full calendar days BETWEEN order and pickup date
    // So first available pickup = today + minLeadDays + 1
    let cursor = dayjs(todayInStoreTimezone).add(minLeadDays + 1, 'day').startOf('day');
    
    // Generate all dates in the range
    for (let i = 0; i < safeMax + 7; i++) { // Add extra days to ensure we have enough selectable dates
        dates.push(cursor.clone());
        cursor = cursor.add(1, 'day');
    }
    
    return dates;
};

const generateSelectableDates = (isValidWorkingDate, minLeadDays, maxLeadDays, storeTimezone) => {
    // Get all dates in range
    const allDates = generateAllDatesInRange(minLeadDays, maxLeadDays, storeTimezone);
    
    // Filter to only include valid working dates
    const validDates = allDates.filter(date => {
        // Explicitly check if the date is valid
        const isValid = isValidWorkingDate(date);
        return isValid;
    });
    
    return validDates;
};

const generateTimeSlots = (selectedDate, openH = 0, openM = 0, closeH = 23, closeM = 45) => {
    if (!selectedDate) return [];

    const start = selectedDate
        .hour(openH)
        .minute(openM)
        .second(0)
        .millisecond(0);
    const end = selectedDate
        .hour(closeH)
        .minute(closeM)
        .second(0)
        .millisecond(0);

    if (!start.isBefore(end)) return [];

    const slots = [];
    let cursor = start.clone();

    while (cursor.isBefore(end)) {
        const next = cursor.add(TIME_SLOT_INTERVAL_MINUTES, 'minute');
        if (next.isAfter(end)) break;
        slots.push({ start: cursor, end: next });
        cursor = next;
    }

    return slots;
};

const CustomMobileDateTimePicker = ({
    value,
    onChange,
    isValidWorkingDate,
    openH,
    openM,
    closeH,
    closeM,
    clampToWindow,
    preOrderMinLeadTime,
    preOrderMaxLeadTime,
    mobile = false,
    direct = false, // New prop to show dialog directly without TextField
    onClose,
    order,
    storeTimezone
}) => {
    const { strings } = useContext(LanguageContext);
    const isReOrder = useSelector(state => state.orders.get('isReOrder'))
    const [open, setOpen] = useState(false);
    const [draftDate, setDraftDate] = useState(value ? dayjs(value) : null);
    const [draftSlot, setDraftSlot] = useState(value ? dayjs(value) : null);
    const [showBasketUpdateDialog, setShowBasketUpdateDialog] = useState(false);
    const dispatch = useDispatch()
    const location = useLocation();
    const basketItems = useSelector(state => state.basket.get('basketItems'));
    const itemsAddedToCart = basketItems?.toJS() || null;
    const isCheckoutScreen = location.pathname.indexOf('/checkout') === 0;
    const shouldShowPreOrder = !isDeliveryMode() && Util.checkForPreOrderItems(itemsAddedToCart);
    useEffect(() => {
        setDraftDate(value ? dayjs(value) : null);
        setDraftSlot(value ? dayjs(value) : null);
    }, [value]);

    // Control open state when direct prop is used
    useEffect(() => {
        if (direct) {
            setOpen(true);
        }
    }, [direct]);

    const minLeadDays = getSafeLeadValue(preOrderMinLeadTime, 0);
    const maxLeadDays = getSafeLeadValue(preOrderMaxLeadTime, 14);

    const selectableDates = useMemo(
        () => generateSelectableDates(isValidWorkingDate, minLeadDays, maxLeadDays, storeTimezone),
        [isValidWorkingDate, minLeadDays, maxLeadDays, storeTimezone]
    );

    const timeSlots = useMemo(
        () => generateTimeSlots(draftDate, openH, openM, closeH, closeM),
        [draftDate, openH, openM, closeH, closeM]
    );

    useEffect(() => {
        if (!open) return;
        if (!draftDate && selectableDates.length) {
            setDraftDate(selectableDates[0]);
        }
    }, [open, selectableDates, draftDate]);

    useEffect(() => {
        if (!draftDate || !timeSlots.length) return;
        if (!draftSlot || !draftSlot.isSame(draftDate, 'day')) {
            setDraftSlot(timeSlots[0]?.start ?? null);
        }
    }, [draftDate, draftSlot, timeSlots]);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setDraftDate(value ? dayjs(value) : null);
        setDraftSlot(value ? dayjs(value) : null);
        onClose?.();
    };

    const proceedWithApply = () => {
        if (!draftSlot) return;
        const adjusted = clampToWindow ? clampToWindow(draftSlot.clone()) : draftSlot;
        onChange?.(adjusted.toDate());
        setOpen(false);
        setShowBasketUpdateDialog(false);
        onClose?.();
        if(isReOrder)
            dispatch(reorder(order))
    };

    const handleApply = () => {
        if (!draftSlot) return;
        if (isCheckoutScreen && shouldShowPreOrder) {
            setShowBasketUpdateDialog(true);
            return;
        }
        proceedWithApply();
    };

    // Format value without timezone conversion - stored values are already in store local time
    const formattedValue = value 
        ? dayjs(value).format('MM/DD/YYYY hh:mm A')
        : '';

    const dialogPaperClass = clsx('schedule-picker__paper', {
        'schedule-picker__paper--desktop': !mobile,
        'schedule-picker__paper--mobile': mobile,
    });

    const dialogContentClass = clsx('schedule-picker__content', {
        'schedule-picker__content--desktop': !mobile,
        'schedule-picker__content--mobile': mobile,
    });

    const dialogActionsClass = clsx('schedule-picker__actions', {
        'schedule-picker__actions--desktop': !mobile,
        'schedule-picker__actions--mobile': mobile,
    });

    const datesWrapperClass = clsx('schedule-picker__dates', {
        'schedule-picker__dates--desktop': !mobile,
        'schedule-picker__dates--mobile': mobile,
    });

    const slotsWrapperClass = clsx('schedule-picker__slots', {
        'schedule-picker__slots--desktop': !mobile,
        'schedule-picker__slots--mobile': mobile,
    });

    if (showBasketUpdateDialog) {
        return (
            <BasketUpdateDialog
                onContinue={() => {
                    proceedWithApply();
                }}
                onKeepPickupTime={() => {
                    setShowBasketUpdateDialog(false);
                }}
            />
        );
    }

    return (
        <>
            {!direct && (
                <TextField
                    fullWidth
                    value={formattedValue}
                    onClick={handleOpen}
                    label={strings.pickupDateTime}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton edge="end" onClick={handleOpen}>
                                    <EventIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#ccc' },
                            '&:hover fieldset': { borderColor: '#00851f' },
                            '&.Mui-focused fieldset': { borderColor: '#00851f' },
                        },
                        '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.6)' },
                        '& label.Mui-focused': { color: '#00851f' },
                    }}
                />
            )}

            {open && (
                <Dialog
                    open={open}
                    onClose={handleClose}
                    maxWidth={false}
                    fullWidth={false}
                    PaperProps={{ className: dialogPaperClass }}
                >
                <DialogContent className={dialogContentClass}>
                    <div className="schedule-picker__header">
                        <p className="schedule-picker__title">{strings.schedulePickUpTime}</p>
                    </div>

                    <div className={datesWrapperClass}>
                        {generateAllDatesInRange(minLeadDays, maxLeadDays, storeTimezone).slice(0, 14).map((date) => {
                            // Check if the day is in workingDays
                            const dayOfWeek = date.format('ddd');
                            const isSelectable = isValidWorkingDate(date);
                            const isSelected = draftDate && date.isSame(draftDate, 'day');
                            return (
                                <button
                                    key={date.toString()}
                                    type="button"
                                    className={clsx('schedule-picker__date-chip', {
                                        'schedule-picker__date-chip--selected': isSelected,
                                        'schedule-picker__date-chip--disabled': !isSelectable
                                    })}
                                    onClick={() => isSelectable && setDraftDate(date)}
                                    disabled={!isSelectable}
                                >
                                    <span className="schedule-picker__date-chip-day">{dayOfWeek}</span>
                                    <span className="schedule-picker__date-chip-date">{date.format('MMM D')}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className={slotsWrapperClass}>
                        {timeSlots.map((slot) => {
                            const isSelected = draftSlot && slot.start.isSame(draftSlot);
                            return (
                                <button
                                    type="button"
                                    key={slot.start.toString()}
                                    className={clsx('schedule-picker__slot', {
                                        'schedule-picker__slot--selected': isSelected,
                                    })}
                                    onClick={() => setDraftSlot(slot.start)}
                                >
                                    <span className="schedule-picker__slot-label">
                                        {`${slot.start.format('h:mm A')} - ${slot.end.format('h:mm A')}`}
                                    </span>
                                    <span className="schedule-picker__slot-radio" />
                                </button>
                            );
                        })}
                    </div>
                    <DialogActions className={dialogActionsClass}>
                        {mobile ? (
                            <>
                                <Button
                                    variant="contained"
                                    onClick={handleApply}
                                    disabled={!draftSlot}
                                >
                                    {strings.applyButton}
                                </Button>
                                <Button variant="outlined" onClick={handleClose}>
                                    {strings.cancelBtn}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outlined" onClick={handleClose}>
                                    {strings.cancelBtn}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleApply}
                                    disabled={!draftSlot}
                                >
                                    {strings.applyButton}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </DialogContent>
            </Dialog>
            )}
        </>
    );
};

export default CustomMobileDateTimePicker;
